import * as XLSX from 'xlsx'

export const POS_PROMOTION_EXPORT_HEADERS = [
  'Promotion ID',
  'Article Code',
  'Product',
  'Type',
  'Actual MRP',
  'Promo price',
  'Unit',
  'Priority',
  'Valid From',
  'Valid To',
  'Status',
  'Min Invoice',
  'Price check',
]

export const PROMO_PRICE_ABOVE_MRP_LABEL = 'Promo price above MRP'

export function formatPromotionDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatPromoPrice(promo) {
  const value = Number(promo?.discount_value) || 0
  const unit = Number(promo?.discount_unit) || 0
  switch (promo?.type) {
    case 'tp':
      return `৳${value.toFixed(2)}`
    case 'percentage':
      return `${value}%`
    case 'discount':
      return `৳${value.toFixed(2)} / ${unit}`
    case 'bonus':
      return `Buy ${unit} get ${value} free`
    default:
      return `${value}`
  }
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

/** Unit promo price for price-bearing promo types. Null when it cannot be compared. */
export function promoUnitPrice(promo) {
  const mrp = Number(promo?.actual_mrp)
  if (!Number.isFinite(mrp) || mrp <= 0) return null
  const discountValue = Number(promo?.discount_value)
  if (!Number.isFinite(discountValue)) return null
  const discountUnit = Number(promo?.discount_unit)
  const unit = discountUnit > 0 ? discountUnit : 1

  switch (promo?.type) {
    case 'tp':
      return roundMoney(discountValue)
    case 'percentage':
      return roundMoney(mrp * (1 - discountValue / 100))
    case 'discount':
      return roundMoney(mrp - discountValue / unit)
    default:
      return null
  }
}

/** True when the promo price is strictly above catalog MRP. */
export function isPromoPriceAboveMrp(promo) {
  const price = promoUnitPrice(promo)
  const mrp = roundMoney(promo?.actual_mrp)
  if (price == null || !Number.isFinite(mrp) || mrp <= 0) return false
  return price > mrp
}

export function formatActualMrp(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) return null
  return Number(amount.toFixed(2))
}

export function formatMinInvoice(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) return ''
  return Number(amount.toFixed(2))
}

export function buildPosPromotionExportRow(promo) {
  const actualMrp = formatActualMrp(promo?.actual_mrp)
  return {
    'Promotion ID': promo?.promotion_id ?? '',
    'Article Code': promo?.sku ?? '',
    Product: promo?.product_name || '',
    Type: promo?.type || '',
    'Actual MRP': actualMrp == null ? '' : actualMrp,
    'Promo price': formatPromoPrice(promo),
    Unit: promo?.discount_unit ?? '',
    Priority: promo?.priority ?? '',
    'Valid From': formatPromotionDate(promo?.valid_from),
    'Valid To': formatPromotionDate(promo?.valid_to),
    Status: promo?.status || '',
    'Min Invoice': formatMinInvoice(promo?.min_invoice_value),
    'Price check': isPromoPriceAboveMrp(promo) ? PROMO_PRICE_ABOVE_MRP_LABEL : '',
  }
}

export function buildPosPromotionsWorkbook(promos) {
  const rows = (promos || []).map(buildPosPromotionExportRow)
  const sheet = XLSX.utils.json_to_sheet(
    rows.length > 0 ? rows : [{}],
    { header: POS_PROMOTION_EXPORT_HEADERS },
  )
  if (rows.length === 0) {
    const emptyRow = POS_PROMOTION_EXPORT_HEADERS.map(() => '')
    XLSX.utils.sheet_add_aoa(sheet, [emptyRow], { origin: 'A2' })
  }
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'POS Promotions')
  return workbook
}

export function downloadPosPromotionsExcel(promos, filename) {
  const workbook = buildPosPromotionsWorkbook(promos)
  XLSX.writeFile(workbook, filename)
}
